import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { FilterFormLoading, PanelLoading } from "~/components/loading";
import { ShareButton } from "~/components/share-button";
import type { ViewState } from "~/components/timeline";

import type {
  EntityListFilters,
  EntitySortOrder,
  EntityView,
  MapViewport,
  SortDirection,
} from "~/features/entities/types";
import { AreaPeek } from "~/features/recordGeoItems";
import { EntitiesMapView } from "./EntitiesMapView";
import { EntitiesSearchForm } from "./EntitiesSearchForm";
import { EntitiesTable } from "./EntitiesTable";
import { EntitiesTimelineView } from "./EntitiesTimelineView";
import { EntitiesViewSwitcher } from "./EntitiesViewSwitcher";
import { EntityPeek } from "./EntityPeek";

interface EntitiesPresenterProps {
  view: EntityView;
  onViewChange: (next: EntityView) => void;
  filters: EntityListFilters;
  onApplyFilters: (next: EntityListFilters) => void;
  sortOrder: EntitySortOrder;
  direction: SortDirection;
  onSortingChange: (order: EntitySortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
  onSelectArea: (id: string, geoItemId: string) => void;
  onAddCategory: (categoryId: string) => void;
  selectedEntity: string | null;
  selectedGeoItem: string | null;
  onCloseSelected: () => void;
  initialTimelineViewState: ViewState | undefined;
  onTimelineViewStateChange: (centerYear: number, scale: number) => void;
  initialMapYearRange: [number, number] | undefined;
  onMapYearRangeChange: (range: [number, number]) => void;
  initialMapViewport: MapViewport | undefined;
  onMapViewportChange: (viewport: MapViewport) => void;
}

export function EntitiesPresenter({
  view,
  onViewChange,
  filters,
  onApplyFilters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
  onSelectArea,
  onAddCategory,
  selectedEntity,
  selectedGeoItem,
  onCloseSelected,
  initialTimelineViewState,
  onTimelineViewStateChange,
  initialMapYearRange,
  onMapYearRangeChange,
  initialMapViewport,
  onMapViewportChange,
}: EntitiesPresenterProps) {
  const { t } = useTranslation();

  // Filter/search changes happen via a React Router navigation, which RR wraps in a
  // transition — so an in-place re-suspense would keep the stale table on screen instead
  // of showing the fallback. Keying the Suspense boundary by the filter signature remounts
  // it, forcing the shimmer. Sort/paging keep the key stable, so they fall through to the
  // table's dimmed-rows pending state instead.
  const filterKey = JSON.stringify([
    filters.idOrName,
    filters.categoryIds,
    filters.referenceIds,
    filters.excludeDeactivated,
    filters.gdtfFrom,
    filters.gdtfTo,
  ]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.record.entity.modelNames")}</h1>
        <div className="flex items-center gap-2">
          <ShareButton />
          <EntitiesViewSwitcher value={view} onChange={onViewChange} />
        </div>
      </div>

      <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0 overflow-hidden border-r border-border-default pr-4">
          <Suspense fallback={<FilterFormLoading />}>
            <EntitiesSearchForm filters={filters} onApply={onApplyFilters} />
          </Suspense>
        </aside>

        <main className="min-w-0">
          {view === "table" && (
            <Suspense key={filterKey} fallback={<PanelLoading />}>
              <EntitiesTable
                filters={filters}
                sortOrder={sortOrder}
                direction={direction}
                onSortingChange={onSortingChange}
                pageIndex={pageIndex}
                onPageIndexChange={onPageIndexChange}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                isPending={isPending}
                onSelect={onSelect}
                onSelectArea={onSelectArea}
                onAddCategory={onAddCategory}
              />
            </Suspense>
          )}
          {view === "timeline" && (
            <Suspense fallback={<PanelLoading />}>
              <EntitiesTimelineView
                filters={filters}
                onSelect={onSelect}
                initialViewState={initialTimelineViewState}
                onViewStateChange={onTimelineViewStateChange}
              />
            </Suspense>
          )}
          {view === "map" && (
            <Suspense fallback={<PanelLoading />}>
              <EntitiesMapView
                filters={filters}
                onSelect={onSelect}
                selectedRecordId={selectedEntity ?? undefined}
                initialYearRange={initialMapYearRange}
                onYearRangeChange={onMapYearRangeChange}
                initialViewport={initialMapViewport}
                onViewportChange={onMapViewportChange}
              />
            </Suspense>
          )}
        </main>
      </div>

      <EntityPeek id={selectedEntity && !selectedGeoItem ? selectedEntity : null} onClose={onCloseSelected} />
      <AreaPeek
        recordId={selectedEntity && selectedGeoItem ? selectedEntity : null}
        geoItemId={selectedGeoItem ?? undefined}
        onClose={onCloseSelected}
      />
    </div>
  );
}
